import type { DevicePosition } from "../../utils/geolocation";

export type StoreEvaluationAnswer = {
  questionId: string;
  booleanValue?: boolean | null;
  intValue?: number | null;
  textValue?: string | null;
  dateValue?: string | null;
  optionId?: string | null;
  optionIds?: string[] | null;
  question?: {
    id?: string;
    prompt: string;
    type: "BOOLEAN" | "SCALE" | "TEXT" | "DATE" | "SINGLE_CHOICE" | "MULTI_CHOICE" | "IMAGE_UPLOAD";
    order: number;
    minValue?: number | null;
    maxValue?: number | null;
    options?: { id: string; label: string }[];
  } | null;
  option?: { id: string; label: string } | null;
  images?: { id: string; assetId: string }[];
};

export type StoreEvaluation = {
  id: string;
  storeId: string;
  formId: string;
  referenceDate: string;
  answeredAt: string;
  status?: string | null;
  score01?: number | null;
  stars?: number | null;
  submittedAt?: string | null;
  updatedAt?: string | null;
  entryPhotoAssetId?: string | null;
  entryPhotoAt?: string | null;
  exitPhotoAssetId?: string | null;
  exitPhotoAt?: string | null;
  store?: { id: string; nomeFantasia: string; endereco?: string | null; estado?: string | null } | null;
  form?: { id: string; category?: string | null; title?: string } | null;
  formType?: { name: string } | null;
  answeredBy?: { id: string; nomeCompleto?: string | null } | null;
  answers?: StoreEvaluationAnswer[];
};

// ---------------------------------------------------------------------------
// Base URL helpers (copiado do padrão supervisorApi.ts)
// ---------------------------------------------------------------------------

// "" = mesma origem (relativo). Dev: proxy do package.json; Prod: rewrite do firebase.json.
const DEFAULT_BASE = "";
const DEFAULT_PREFIX = "/api";

function tryGetViteEnv(key: string): string | undefined {
  try {
    return (import.meta as any)?.env?.[key];
  } catch {
    return undefined;
  }
}

function tryGetCraEnv(key: string): string | undefined {
  try {
    return (process as any)?.env?.[key];
  } catch {
    return undefined;
  }
}

function normalizeBase(base: string) {
  return String(base).replace(/\/$/, "");
}

function normalizePrefix(prefix: string) {
  const p = String(prefix || "");
  if (!p) return "";
  return p.startsWith("/") ? p : `/${p}`;
}

export function getApiBase(): string {
  const base =
    tryGetViteEnv("VITE_API_BASE") ??
    tryGetCraEnv("REACT_APP_API_BASE") ??
    DEFAULT_BASE;

  const prefix =
    tryGetViteEnv("VITE_API_PREFIX") ??
    tryGetCraEnv("REACT_APP_API_PREFIX") ??
    DEFAULT_PREFIX;

  return `${normalizeBase(base)}${normalizePrefix(prefix)}`;
}

async function readErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const json = JSON.parse(text);
    return json?.message ?? text ?? "Erro na requisição.";
  } catch {
    return text || "Erro na requisição.";
  }
}

function normalizeBearer(token: string): string {
  const t = String(token ?? "").trim();
  return t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
}

function buildUrl(path: string, query?: string | URLSearchParams): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const base = `${getApiBase()}${cleanPath}`;

  if (!query) return base;

  const qs = typeof query === "string" ? query.replace(/^\?/, "") : query.toString();
  return qs ? `${base}?${qs}` : base;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function listStoreEvaluationsApi(args: {
  token: string;
  storeId: string;
  referenceDate?: string;
  referenceFrom?: string;
  referenceTo?: string;
  answeredById?: string;
  formId?: string;
  category?: string;
  includeAnswers?: boolean;
}): Promise<StoreEvaluation[]> {
  const token = normalizeBearer(args.token);

  const qs = new URLSearchParams();

  if (args.referenceDate) qs.set("referenceDate", args.referenceDate);
  if (args.referenceFrom) qs.set("referenceFrom", args.referenceFrom);
  if (args.referenceTo) qs.set("referenceTo", args.referenceTo);
  if (args.answeredById) qs.set("answeredById", args.answeredById);
  if (args.formId) qs.set("formId", args.formId);
  if (args.category) qs.set("category", args.category);
  if (args.includeAnswers !== undefined) qs.set("includeAnswers", String(args.includeAnswers));

  const res = await fetch(
    buildUrl(`/stores/${encodeURIComponent(args.storeId)}/evaluations`, qs),
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    },
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));

  const data = await res.json();
  return Array.isArray(data) ? data : (data.items ?? []);
}

export async function getStoreEvaluationByIdApi(args: {
  token: string;
  evaluationId: string;
}): Promise<StoreEvaluation> {
  const token = normalizeBearer(args.token);

  const res = await fetch(
    buildUrl(`/store-evaluations/${encodeURIComponent(args.evaluationId)}`),
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    },
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));

  return await res.json();
}

export async function openStoreEvaluationApi(args: {
  token: string;
  storeId: string;
  formId: string;
  referenceDate: string;
}): Promise<any> {
  const token = normalizeBearer(args.token);
  const res = await fetch(
    buildUrl(`/stores/${encodeURIComponent(args.storeId)}/evaluations/open`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ formId: args.formId, referenceDate: args.referenceDate }),
    }
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function setEntryPhotoApi(args: {
  token: string;
  evaluationId: string;
  assetId: string;
  /** Posição do celular: o backend confere o raio da loja quando `avaliacao.exigirLocalizacao` está ligada. */
  position?: DevicePosition | null;
}): Promise<any> {
  const token = normalizeBearer(args.token);
  const res = await fetch(
    buildUrl(`/store-evaluations/${encodeURIComponent(args.evaluationId)}/entry-photo`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ assetId: args.assetId, ...(args.position ?? {}) }),
    }
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function submitStoreEvaluationApi(args: {
  token: string;
  evaluationId: string;
  exitPhotoAssetId: string;
  answers: any[];
  position?: DevicePosition | null;
}): Promise<any> {
  const token = normalizeBearer(args.token);
  const res = await fetch(
    buildUrl(`/store-evaluations/${encodeURIComponent(args.evaluationId)}/submit`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        exitPhotoAssetId: args.exitPhotoAssetId,
        answers: args.answers,
        ...(args.position ?? {}),
      }),
    }
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function resetStoreEvaluationApi(args: {
  token: string;
  evaluationId: string;
}): Promise<any> {
  const token = normalizeBearer(args.token);
  const res = await fetch(
    buildUrl(`/store-evaluations/${encodeURIComponent(args.evaluationId)}/reset`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    }
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

/**
 * Rascunho da auditoria aberta: só as respostas que mudaram (resposta vazia
 * apaga a gravada) e, quando mudou, a foto de saída (`null` limpa).
 * `keepalive` deixa o envio terminar com a aba fechando.
 */
export async function saveStoreEvaluationDraftApi(args: {
  token: string;
  evaluationId: string;
  answers?: any[];
  exitPhotoAssetId?: string | null;
  keepalive?: boolean;
}): Promise<{ id: string; updatedAt: string }> {
  const token = normalizeBearer(args.token);
  const body: Record<string, unknown> = {};
  if (args.answers) body.answers = args.answers;
  if (args.exitPhotoAssetId !== undefined) body.exitPhotoAssetId = args.exitPhotoAssetId;

  const res = await fetch(
    buildUrl(`/store-evaluations/${encodeURIComponent(args.evaluationId)}/draft`),
    {
      method: "PATCH",
      keepalive: args.keepalive,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    }
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export type MyOpenStoreEvaluation = {
  id: string;
  storeId: string;
  formId: string;
  referenceDate: string;
  entryPhotoAssetId?: string | null;
  exitPhotoAssetId?: string | null;
  updatedAt: string;
  store: { id: string; nomeFantasia: string; endereco?: string | null; estado?: string | null };
  form: { id: string; title: string };
  _count: { answers: number };
};

/** Auditorias abertas de quem está logado, da mais recente para a mais antiga. */
export async function listMyOpenStoreEvaluationsApi(args: { token: string }): Promise<MyOpenStoreEvaluation[]> {
  const token = normalizeBearer(args.token);
  const res = await fetch(buildUrl("/store-evaluations/open/mine"), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

function appendFilterParams(qs: URLSearchParams, params: any) {
  if (!params) return;
  if (params.storeId) qs.set("storeId", params.storeId);
  if (params.estado) qs.set("estado", params.estado.toUpperCase());
  if (params.answeredById) qs.set("answeredById", params.answeredById);
  if (params.formId) qs.set("formId", params.formId);
  if (params.referenceFrom) qs.set("referenceFrom", params.referenceFrom);
  if (params.referenceTo) qs.set("referenceTo", params.referenceTo);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.months) qs.set("months", String(params.months));
  if (params.page) qs.set("page", String(params.page));
}

export async function getQuestionDeficienciesApi(
  token: string,
  params: any = {}
): Promise<any> {
  const t = normalizeBearer(token);
  const qs = new URLSearchParams();
  appendFilterParams(qs, params);

  const res = await fetch(buildUrl("/store-evaluations/reports/question-deficiencies", qs), {
    headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function getStoreRankingApi(
  token: string,
  params: any = {}
): Promise<any> {
  const t = normalizeBearer(token);
  const qs = new URLSearchParams();
  appendFilterParams(qs, params);

  const res = await fetch(buildUrl("/store-evaluations/reports/store-ranking", qs), {
    headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function getScoreEvolutionApi(
  token: string,
  params: any = {}
): Promise<any> {
  const t = normalizeBearer(token);
  const qs = new URLSearchParams();
  appendFilterParams(qs, params);

  const res = await fetch(buildUrl("/store-evaluations/reports/score-evolution", qs), {
    headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function getScoreTrendApi(
  token: string,
  params: any = {}
): Promise<any> {
  const t = normalizeBearer(token);
  const qs = new URLSearchParams();
  appendFilterParams(qs, params);

  const res = await fetch(buildUrl("/store-evaluations/reports/score-trend", qs), {
    headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}

export async function getStoreQuestionHeatmapApi(
  token: string,
  params: any = {}
): Promise<any> {
  const t = normalizeBearer(token);
  const qs = new URLSearchParams();
  appendFilterParams(qs, params);

  const res = await fetch(buildUrl("/store-evaluations/reports/store-question-heatmap", qs), {
    headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));
  return await res.json();
}
